import { ApiParams } from '@sierralabs/react-material-ui';
import { useCallback } from 'react';
import {
  ApiResult,
  FetchApiCallback,
  useApiDelete,
  useApiPatch,
  useApiGet,
  useApiPost
} from '../../common/api/Api.hooks';
import { useDownloadBlob } from '../../common/blob.hook';
import { useIdSearchableFetch } from '../../common/entityId.hooks';
import { PaginatedList } from '../../common/PaginatedList.interface';
import { WorkflowEntity } from '../workflow/Workflow.interface';
import { Capa, CapaStat } from './Capa.interface';

const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export function useGetCapaList(): [
  ApiResult<PaginatedList<Capa>>,
  FetchApiCallback
] {
  const { data, isLoading, error, get } =
    useApiGet<PaginatedList<Capa>>('capa');
  const getCapaList = useIdSearchableFetch(get, {
    from: 'filter',
    entity: WorkflowEntity.Capa
  });
  return [{ data, isLoading, error }, getCapaList];
}

export function useGetCapa(id: number): [ApiResult<Capa>, FetchApiCallback] {
  const { data, isLoading, error, get } = useApiGet<Capa>('capa/' + id);
  return [{ data, isLoading, error }, get];
}

export function useCreateCapa(): [ApiResult<Capa>, FetchApiCallback] {
  const { data, isLoading, error, post } = useApiPost<Capa>('capa/');
  return [{ data, isLoading, error }, post];
}

export function useUpdateCapa(id: number): [ApiResult<Capa>, FetchApiCallback] {
  const { data, isLoading, error, patch } = useApiPatch<Capa>(`capa/${id}`);
  return [{ data, isLoading, error }, patch];
}
export function useExportCapaDocument(
  id: number
): [
  ApiResult<any>,
  (extension: 'docx' | 'pdf') => void,
  (fileName: string) => void
] {
  const { get, ...result } = useApiGet<Capa>(`capa/${id}/export`);
  const { response } = result;
  const { setBlob, downloadDocument } = useDownloadBlob(response);

  const getDocument = useCallback(
    (extension: 'docx' | 'pdf') => {
      // remove old document
      setBlob(undefined);
      get({ extension, timeZone });
    },
    [get]
  );

  return [result, getDocument, downloadDocument];
}

export function useDeleteCapa(): [ApiResult<Capa>, FetchApiCallback] {
  const { data, isLoading, error, del } = useApiDelete<Capa>('capa/');
  return [{ data, isLoading, error }, del];
}

export function useFindCapaByTask(taskId: number) {
  const { data, isLoading, error, get } = useApiGet<Capa>(
    'capa/task/' + taskId
  );
  return { data, isLoading, error, get };
}

export function useGetCapaStats(): [ApiResult<CapaStat[]>, FetchApiCallback] {
  const { get, ...result } = useApiGet<CapaStat[]>('capa/stats');
  return [result, get];
}
